import { FC } from 'react';
import {
  Chip,
  Button,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
  LinkAsButton,
  ReactionsPreview,
} from 'melp-design/components';
import { Divider, List, ListItem, Stack } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  DateRangeFilter,
  EmployeesGroupsFilter,
  FilterButton,
  Filters,
  NewsCategoryFilter,
  SelectFilter,
} from 'components/filters';
import { APP, ROUTES } from 'config';
import {
  COLOR_BY_STATUS,
  newsFiltersSchema,
  newsSendMethods,
  newsStatuses,
  useBulkDeleteNews,
  useBulkUpdateNews,
  useCreateNew,
  useExportToXls,
  useNews,
} from 'store/news';
import { useModalContext } from 'store/modal';
import { binaries } from 'types/Common';
import { DownloadXlsAction } from 'components/actions';
import { usePredefinedToasts } from 'utils/Toast';
import { useSearchParams } from 'utils/navigation';
import { NewActions, NewsThreadModal } from 'containers/news';
import { ReactionsModal } from 'containers/reactions';
import { useConfirm } from 'store/confirm';
import { NewNameModal } from '../new-details/atoms';

export const NewsList: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } =
    useSearchParams(newsFiltersSchema);

  const { data: news, isLoading } = useNews(filters);

  const { mutate: exportToXls, isLoading: isExporting } = useExportToXls();
  const { mutate: createNew, isLoading: isCreating } = useCreateNew();
  const { mutate: bulkUpdateNews, isLoading: isUpdating } = useBulkUpdateNews();
  const { mutate: bulkDeleteNews, isLoading: isDeleting } = useBulkDeleteNews();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap="10px">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap="15px"
          >
            <Button
              label={t('news.add_news')}
              onClick={async () => {
                const response = await openModal({
                  component: NewNameModal,
                });

                if (response.action === 'CONFIRM') {
                  createNew(
                    { name: response.name },
                    {
                      onSuccess: ({ data }) => {
                        predefinedToasts.success.added();
                        history.push(
                          `${ROUTES.news.details.replace(
                            ':id',
                            data.id,
                          )}?guided=true`,
                        );
                      },
                    },
                  );
                }
              }}
              disabled={isCreating}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
          >
            <ListTotal total={news?.total} />

            <Stack direction="row" alignItems="center" gap="10px">
              <Filters
                value={filters}
                initialValues={newsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: v })}
                onChangeCallback={() => setSelectedRowIds({})}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('benefits.benefit_types')}
                      value={value.categories}
                      initialValue={initialValues.categories}
                      onChange={(v) => setFilter('categories', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <NewsCategoryFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('news.notificationsSectionTitle')}
                      value={value.sendMethods}
                      initialValue={initialValues.sendMethods}
                      onChange={(v) => setFilter('sendMethods', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          multiple
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={newsSendMethods.map((method) => ({
                            label: t(`news.send_method.${method}`),
                            key: method,
                          }))}
                          searchable={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('news.allowComments.label')}
                      value={value.allowComments}
                      initialValue={initialValues.allowComments}
                      onChange={(v) => setFilter('allowComments', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={binaries.map((binary) => ({
                            key: binary,
                            label: t(`common.${binary}`),
                          }))}
                          searchable={false}
                          multiple={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('news.pinned.title')}
                      value={value.pinned}
                      initialValue={initialValues.pinned}
                      onChange={(v) => setFilter('pinned', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={binaries.map((binary) => ({
                            key: binary,
                            label: t(`common.${binary}`),
                          }))}
                          searchable={false}
                          multiple={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('news.published_from')}
                      value={value.publishedAt}
                      initialValue={initialValues.publishedAt}
                      onChange={(v) => setFilter('publishedAt', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('common.status')}
                      value={value.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={newsStatuses.map((status) => ({
                            key: status,
                            label: t(`status.${status}`),
                          }))}
                          searchable={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('entity_type.employeeGroup')}
                      value={value.employeeGroups}
                      initialValue={initialValues.employeeGroups}
                      onChange={(v) => setFilter('employeeGroups', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <EmployeesGroupsFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>
                  </>
                )}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={news?.total ?? 0}
                exportToExcel={() => exportToXls({ filters })}
                loading={isExporting}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={news?.items ?? []}
        isLoading={isLoading}
        stickyHeader
        columns={[
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: newItem } }) => {
              return (
                <Typography color={COLOR_BY_STATUS[newItem.status]}>
                  {t(`status.${newItem.status}`)}
                </Typography>
              );
            },
          },
          {
            id: 'publishDate',
            header: t('common.date'),
            cell: ({ row: { original: newItem } }) =>
              newItem.publishedAt
                ? moment(newItem.publishedAt).format('l')
                : '',
            meta: { sort: true },
          },
          {
            id: 'name',
            header: t('news.title.label'),
            cell: ({ row: { original: newItem } }) => {
              return (
                <Stack spacing={0.25} alignItems="flex-start">
                  {newItem.pinned ? (
                    <Chip label={t('news.pinned.title')} variant="warning" />
                  ) : null}
                  <Link
                    label={newItem.name}
                    to={ROUTES.news.details.replace(':id', newItem.id)}
                  />
                  <ReactionsPreview
                    reactions={newItem.reactions.summary}
                    onClick={() => {
                      openModal({
                        component: ReactionsModal,
                        props: { hostId: newItem.id },
                      });
                    }}
                  />
                </Stack>
              );
            },
          },
          {
            id: 'categories',
            header: t('benefits.benefit_type'),
            cell: ({ row: { original: newItem } }) => {
              return (
                <List sx={{ py: 0 }}>
                  {newItem.categories.map((c) => (
                    <ListItem sx={{ p: 0, mb: 1 }} key={c.id}>
                      <Link
                        label={c.name}
                        to={ROUTES.news.categories.details.replace(':id', c.id)}
                      />
                    </ListItem>
                  ))}
                </List>
              );
            },
          },
          {
            id: 'sentTo',
            header: t('news.sent_to'),
            cell: ({ row: { original: newItem } }) => {
              return newItem.sentCount;
            },
            meta: { align: 'right', sort: true },
          },
          {
            id: 'views',
            header: t('news.seen'),
            cell: ({ row: { original: newItem } }) => {
              return newItem.seenCount;
            },
            meta: { align: 'right', sort: true },
          },
          {
            id: 'comments',
            header: '💬',
            cell: ({ row: { original: newItem } }) => {
              return (
                <LinkAsButton
                  label={newItem.commentsCount}
                  onClick={() => {
                    openModal({
                      component: NewsThreadModal,
                      props: {
                        newId: newItem.id,
                      },
                      size: 'xl',
                    });
                  }}
                />
              );
            },
            meta: { align: 'right', sort: true },
          },
          {
            id: 'reactions',
            header: '👍',
            cell: ({ row: { original: newItem } }) => {
              return (
                <LinkAsButton
                  label={newItem.reactions.total}
                  onClick={() => {
                    openModal({
                      component: ReactionsModal,
                      props: { hostId: newItem.id },
                    });
                  }}
                />
              );
            },
            meta: { align: 'right', sort: true },
          },
          {
            id: 'actions',
            cell: ({ row: { original: newItem } }) => {
              return (
                <NewActions
                  id={newItem.id}
                  status={newItem.status}
                  name={newItem.name}
                  reactions={newItem.reactions}
                  type="compact"
                />
              );
            },
          },
        ]}
        rows={{
          inactive: ({ original: newItem }) => newItem.status === 'inactive',
        }}
        pagination={
          news && {
            count: news.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: [
            {
              label: t('common.hide'),
              onClick: () => {
                bulkUpdateNews(
                  {
                    ids: Object.keys(selectedRowIds),
                    data: { inactiveAt: moment().toISOString(true) },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('comments.actions.enable'),
              onClick: () => {
                bulkUpdateNews(
                  {
                    ids: Object.keys(selectedRowIds),
                    data: { allowComments: true },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('comments.actions.disable'),
              onClick: () => {
                bulkUpdateNews(
                  {
                    ids: Object.keys(selectedRowIds),
                    data: { allowComments: false },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('actions.delete'),
              onClick: async () => {
                const { confirmed } = await confirmation('');

                if (confirmed) {
                  bulkDeleteNews(
                    { ids: Object.keys(selectedRowIds) },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.deleted();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
              color: 'error',
            },
          ],
          isLoading: isUpdating || isDeleting,
        }}
      />
    </TableWrapper>
  );
};
